import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, differenceInDays, startOfDay, addDays } from 'date-fns';
import { ArrowLeft, ArrowRight, Check, AlertCircle, Shield, Truck, RotateCcw, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAILY_RATE = 59.99;
const MIN_RENTAL_DAYS = 3;
const EXPEDITED_SHIPPING_FEE = 50;
const BLOCKED_DAYS = 2;
const EXPEDITED_CUTOFF_DAYS = 6;
const INSURANCE_DAILY_RATE = 25;

// Sizes
const HELMET_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const JACKET_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const GLOVE_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addItem, setDates } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookedDates, setBookedDates] = useState([]);
  const [selectedMainImage, setSelectedMainImage] = useState(0);
  
  // Size selections
  const [helmetSize, setHelmetSize] = useState('');
  const [jacketSize, setJacketSize] = useState('');
  const [gloveSize, setGloveSize] = useState('');
  
  // Rental options
  const [selectedDates, setSelectedDates] = useState({ start: null, end: null });
  const [expeditedShipping, setExpeditedShipping] = useState(false);
  const [damageProtection, setDamageProtection] = useState(false);
  
  // Waitlist
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);

  const today = startOfDay(new Date());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, availabilityRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/availability/${productId}`)
        ]);
        setProduct(productRes.data);
        setBookedDates(availabilityRes.data.booked_dates || []);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId]);

  // Check if product is a kit
  const isKit = product?.product_type === 'kit' || product?.title?.toLowerCase().includes('kit');
  const isOutOfStock = product?.is_coming_soon || product?.inventory_quantity === 0;

  // Get shipping tier based on start date
  const getShippingTier = (date) => {
    if (!date) return null;
    const daysFromToday = differenceInDays(startOfDay(date), today);
    if (daysFromToday <= BLOCKED_DAYS) return 'blocked';
    if (daysFromToday <= EXPEDITED_CUTOFF_DAYS) return 'expedited';
    return 'standard';
  };

  const shippingTier = getShippingTier(selectedDates.start);
  const autoExpeditedRequired = shippingTier === 'expedited';
  const effectiveExpeditedShipping = autoExpeditedRequired || expeditedShipping;
  const shippingFee = effectiveExpeditedShipping ? EXPEDITED_SHIPPING_FEE : 0;

  // Calculate pricing
  const pricingDetails = useMemo(() => {
    if (!selectedDates.start || !selectedDates.end || !product) return null;
    
    const days = differenceInDays(selectedDates.end, selectedDates.start) + 1;
    if (days < MIN_RENTAL_DAYS) return null;
    
    const dailyRate = parseFloat(product.price);
    let discountPercent = 0;
    if (days >= 7) discountPercent = 25;
    else if (days >= 3) discountPercent = 15;
    
    const subtotal = days * dailyRate;
    const discountAmount = (subtotal * discountPercent) / 100;
    const rentalTotal = subtotal - discountAmount;
    const insuranceTotal = damageProtection ? (INSURANCE_DAILY_RATE * days) : 0;
    const total = rentalTotal + shippingFee + insuranceTotal;
    
    return {
      days,
      dailyRate,
      discount: discountPercent,
      subtotal,
      discountAmount,
      rentalTotal,
      insuranceTotal,
      shippingFee,
      total
    };
  }, [selectedDates, product, damageProtection, shippingFee]);

  const handleDateSelect = (date) => {
    if (!date) return;
    
    const tier = getShippingTier(date);
    if (tier === 'blocked') {
      toast.error('This date is not available. Please select a date at least 3 days from now.');
      return;
    }
    
    if (!selectedDates.start || (selectedDates.start && selectedDates.end)) {
      const minEnd = addDays(date, MIN_RENTAL_DAYS - 1);
      setSelectedDates({ start: date, end: minEnd });
    } else {
      if (date < selectedDates.start) {
        setSelectedDates({ start: date, end: selectedDates.start });
      } else {
        const days = differenceInDays(date, selectedDates.start) + 1;
        if (days < MIN_RENTAL_DAYS) {
          const minEnd = addDays(selectedDates.start, MIN_RENTAL_DAYS - 1);
          setSelectedDates({ start: selectedDates.start, end: minEnd });
          toast.info(`Minimum rental is ${MIN_RENTAL_DAYS} days`);
        } else {
          setSelectedDates({ start: selectedDates.start, end: date });
        }
      }
    }
  };

  const isDateDisabled = (date) => {
    const d = startOfDay(date);
    const daysFromToday = differenceInDays(d, today);
    if (daysFromToday <= BLOCKED_DAYS) return true;
    if (bookedDates.includes(format(d, 'yyyy-MM-dd'))) return true;
    return false;
  };

  const handleAddToCart = () => {
    if (!selectedDates.start || !selectedDates.end) {
      toast.error('Please select rental dates');
      return;
    }
    addItem(product, { helmetSize, jacketSize, gloveSize });
    setDates(selectedDates.start, selectedDates.end);
    toast.success('Added to cart!');
    navigate('/cart');
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) {
      toast.error('Please enter your email');
      return;
    }
    try {
      await axios.post(`${API}/waitlist`, {
        email: waitlistEmail,
        kit_id: product.id,
        kit_name: product.title
      }).catch(() => {});
      setIsOnWaitlist(true);
      toast.success(`You're on the waitlist for ${product.title}!`);
    } catch (error) {
      setIsOnWaitlist(true);
      toast.success(`You're on the waitlist for ${product.title}!`);
    }
  };

  const canBook = selectedDates.start && selectedDates.end && pricingDetails?.days >= MIN_RENTAL_DAYS;

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-gray-100 animate-pulse"></div>
            <div className="space-y-6">
              <div className="h-4 bg-gray-100 w-1/4"></div>
              <div className="h-10 bg-gray-100 w-3/4"></div>
              <div className="h-24 bg-gray-100 w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2 text-gray-900">Product Not Found</h2>
          <Button onClick={() => navigate('/products')} className="mt-4 bg-[#1a1a1a] text-white hover:bg-[#333]">
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="product-detail-page">
      <div className="px-6 md:px-12 py-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-[#C6A87C] transition-colors mb-8"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Products
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: Images */}
            <div className="space-y-4">
              <div className="flex gap-4">
                {/* Thumbnail Column */}
                {product.images?.length > 1 && (
                  <div className="flex flex-col gap-3 w-20">
                    {product.images.map((img, idx) => (
                      <div 
                        key={idx} 
                        className={`aspect-square bg-white border overflow-hidden cursor-pointer transition-all ${
                          selectedMainImage === idx ? 'border-[#1a1a1a]' : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onClick={() => setSelectedMainImage(idx)}
                      >
                        <img src={img} alt="" className="w-full h-full object-contain p-1" />
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Main Image */}
                <div className="flex-1 relative">
                  <div className="aspect-square bg-white border border-gray-200 overflow-hidden">
                    <img 
                      src={product.images?.[selectedMainImage] || product.image_url} 
                      alt={product.title}
                      className="w-full h-full object-contain p-4"
                    />
                  </div>
                  
                  {/* Coming Soon Badge */}
                  {isOutOfStock && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                      <span className="bg-[#1a1a1a] text-white font-bold uppercase tracking-wider px-6 py-3 text-lg">
                        Coming Soon
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Sidebar */}
            <div className="bg-gray-50 border border-gray-200 p-6 space-y-6">
              {/* Product Title */}
              <div>
                <p className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2">{product.vendor || 'Essential Series'}</p>
                <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-gray-900" data-testid="product-title">
                  {product.title}
                </h1>
                <p className="text-gray-500 mt-2 text-sm">
                  {product.description?.substring(0, 150)}...
                </p>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-2 pb-4 border-b border-gray-200">
                <span className="text-4xl font-bold text-gray-900" data-testid="product-price">
                  ${product.price}
                </span>
                <span className="text-gray-500">/ Day</span>
                <span className="text-xs text-gray-500 ml-2">
                  {MIN_RENTAL_DAYS} days minimum
                </span>
              </div>

              {isOutOfStock ? (
                /* Waitlist Section for Out of Stock */
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 p-6 text-center">
                    <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-gray-900 mb-2">
                      Coming Soon
                    </h3>
                    <p className="text-gray-500 text-sm mb-4">
                      Be the first to know when this kit is available.
                    </p>
                    
                    {!isOnWaitlist ? (
                      !showWaitlistForm ? (
                        <Button
                          onClick={() => setShowWaitlistForm(true)}
                          className="w-full bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider"
                          data-testid="join-waitlist-btn"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Join the Waitlist
                        </Button>
                      ) : (
                        <form onSubmit={handleWaitlistSubmit} className="space-y-3">
                          <input
                            type="email"
                            value={waitlistEmail}
                            onChange={(e) => setWaitlistEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="w-full h-12 px-4 bg-white border border-gray-300 text-sm placeholder:text-gray-400 focus:border-[#1a1a1a] focus:outline-none transition-colors text-gray-900"
                            data-testid="waitlist-email-input"
                          />
                          <Button
                            type="submit"
                            className="w-full bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider"
                            data-testid="submit-waitlist-btn"
                          >
                            Get Early Access
                          </Button>
                        </form>
                      )
                    ) : (
                      <div className="flex items-center justify-center gap-2 py-4 bg-gray-100 border border-gray-200">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm text-gray-700 font-semibold uppercase tracking-wider">
                          On the Waitlist
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Available Product - Full Booking Flow */
                <>
                  {/* Size Selection */}
                  {isKit ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-gray-700 uppercase tracking-widest mb-2 block font-semibold">
                            Helmet Size
                          </label>
                          <Select value={helmetSize} onValueChange={setHelmetSize}>
                            <SelectTrigger className="w-full bg-white border-gray-300 h-12" data-testid="helmet-size-select">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              {HELMET_SIZES.map(size => (
                                <SelectItem key={size} value={size}>{size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-700 uppercase tracking-widest mb-2 block font-semibold">
                            Jacket Size
                          </label>
                          <Select value={jacketSize} onValueChange={setJacketSize}>
                            <SelectTrigger className="w-full bg-white border-gray-300 h-12" data-testid="jacket-size-select">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              {JACKET_SIZES.map(size => (
                                <SelectItem key={size} value={size}>{size}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-700 uppercase tracking-widest mb-2 block font-semibold">
                          Glove Size
                        </label>
                        <Select value={gloveSize} onValueChange={setGloveSize}>
                          <SelectTrigger className="w-full bg-white border-gray-300 h-12" data-testid="glove-size-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200">
                            {GLOVE_SIZES.map(size => (
                              <SelectItem key={size} value={size}>{size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : (
                    product.variants?.length > 0 && (
                      <div>
                        <label className="text-xs text-gray-700 uppercase tracking-widest mb-2 block font-semibold">
                          Size
                        </label>
                        <Select value={helmetSize} onValueChange={setHelmetSize}>
                          <SelectTrigger className="w-full bg-white border-gray-300 h-12" data-testid="size-select">
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200">
                            {product.variants.map(variant => (
                              <SelectItem key={variant.id} value={variant.id}>
                                {variant.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )
                  )}

                  {/* Rental Dates Calendar */}
                  <div className="border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-700 uppercase tracking-widest font-semibold">
                        Rental Dates
                      </span>
                      {pricingDetails && (
                        <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {shippingFee > 0 ? '$50 EXPEDITED' : 'FREE'} SHIPPING
                        </span>
                      )}
                    </div>
                    
                    <Calendar
                      mode="single"
                      selected={selectedDates.start}
                      onSelect={handleDateSelect}
                      disabled={isDateDisabled}
                      modifiers={{
                        selected: (date) => {
                          if (!selectedDates.start || !selectedDates.end) return false;
                          return date >= selectedDates.start && date <= selectedDates.end;
                        }
                      }}
                      modifiersClassNames={{
                        selected: 'bg-[#1a1a1a] text-white'
                      }}
                      className="bg-transparent border-0"
                    />
                    
                    {selectedDates.start && selectedDates.end && (
                      <div className="mt-3 pt-3 border-t border-gray-200 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Selected Period:</span>
                          <span className="font-semibold text-gray-900">
                            {format(selectedDates.start, 'MMM d')} - {format(selectedDates.end, 'MMM d, yyyy')}
                          </span>
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-gray-500">Duration:</span>
                          <span className="font-semibold text-gray-900">{pricingDetails?.days} days</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Damage Protection Toggle */}
                  <div className="flex items-center justify-between p-4 bg-white border border-gray-200">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-gray-500" />
                      <span className="text-sm font-semibold uppercase tracking-wider text-gray-700">Damage Protection</span>
                      <span className="text-xs text-gray-500">${INSURANCE_DAILY_RATE}/day</span>
                    </div>
                    <div 
                      className={`w-12 h-6 rounded-full relative cursor-pointer transition-colors ${
                        damageProtection ? 'bg-[#1a1a1a]' : 'bg-gray-300'
                      }`}
                      onClick={() => setDamageProtection(!damageProtection)}
                      data-testid="damage-protection-toggle"
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                        damageProtection ? 'left-7' : 'left-1'
                      }`} />
                    </div>
                  </div>

                  {/* Pricing Summary */}
                  {pricingDetails && (
                    <div className="border-t border-gray-200 pt-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Rental ({pricingDetails.days} days)</span>
                        <span className="text-gray-900">${pricingDetails.subtotal.toFixed(2)}</span>
                      </div>
                      {pricingDetails.discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount ({pricingDetails.discount}%)</span>
                          <span>-${pricingDetails.discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {pricingDetails.shippingFee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Expedited Shipping</span>
                          <span className="text-gray-900">+${pricingDetails.shippingFee.toFixed(2)}</span>
                        </div>
                      )}
                      {damageProtection && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Damage Protection</span>
                          <span className="text-gray-900">+${pricingDetails.insuranceTotal.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-gray-200 font-bold text-lg">
                        <span className="text-gray-900">Total</span>
                        <span className="text-gray-900">${pricingDetails.total.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Rent Now Button - Dark Style */}
                  <Button
                    onClick={handleAddToCart}
                    disabled={!canBook}
                    className={`w-full font-bold uppercase tracking-wider h-14 text-base ${
                      canBook
                        ? 'bg-[#1a1a1a] text-white hover:bg-[#333]' 
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="rent-now-btn"
                  >
                    {canBook ? (
                      <>
                        Rent Now
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </>
                    ) : (
                      'Select Dates to Book'
                    )}
                  </Button>
                </>
              )}

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Shield className="w-4 h-4 text-[#C6A87C]" />
                  <span>CE Level 2 certified</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Check className="w-4 h-4 text-[#C6A87C]" />
                  <span>Professionally sanitized</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Truck className="w-4 h-4 text-[#C6A87C]" />
                  <span>Free return shipping</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <RotateCcw className="w-4 h-4 text-[#C6A87C]" />
                  <span>2-day return buffer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
